import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Select, InputNumber, Form, Row, Col, Card, message, Space, Divider, Checkbox, Modal, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import AxiosOICStat from 'AxiosOICStat';
import CarryExportService from 'components/exportServices/CarryExportService';
import CategoriesCollapse from 'components/CategoriesCollapse';
import { getIndicatorName } from '../utils/indicatorName';
import { config } from '@fortawesome/fontawesome-svg-core';

const { Option } = Select;

export default function Carry() {
    const [loading, setLoading] = useState(false);
    const submittingRef = useRef(false);
    const [results, setResults] = useState([]);
    const [form] = Form.useForm();
    const indicatorsState = useSelector((state) => state.indicators);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [keyIndicators, setKeyIndicators] = useState([]);
    const [isKeyIndicatorsFetching, setIsKeyIndicatorsFetching] = useState(false);
    const [regionMappingsData, setRegionMappingsData] = useState([]);
    const countriesState = useSelector((state) => state.countries);
    const unitsState = useSelector((state) => state.units);
    const [carryExportService, setCarryExportService] = useState(null);
    const [indicatorSearchFilter, setIndicatorSearchFilter] = useState('');

    const crossCheckNotice = useRef(null);
    useEffect(() => () => crossCheckNotice.current?.destroy(), []);
    const showCrossCheckNotice = () => {
        crossCheckNotice.current = Modal.warning({
            title: 'Aggregation Calculations – Cross-Check Required',
            content: 'Please review and cross-check the aggregation calculations in the OICStat Admin Panel and report any discrepancies.',
        });
    };

    useEffect(() => {
        const fetchRegionMappings = async () => {
            try {
                const response = await AxiosOICStat.get('/region-mappings/');
                setRegionMappingsData(response.data);
            } catch (error) {
                console.error('Failed to fetch region mappings:', error);
            }
        };
        fetchRegionMappings();
    }, []);

    useEffect(() => {
        if (!isKeyIndicatorsFetching) {
            AxiosOICStat.post('/secondary-categories/get-key-indicators').then(res => {
                setKeyIndicators(res.data);
            })
            setIsKeyIndicatorsFetching(true);
        }
    }, [isKeyIndicatorsFetching]);

    const handleCategoryChange = (categoryId, isChecked) => {
        const currentIndicators = form.getFieldValue('indicators') || [];
        const indicatorsInCategory = Object.values(indicatorsState.value || {}).filter(
            (indicator) => indicator.cat_code === categoryId || (indicator.category_codes && indicator.category_codes.includes(categoryId))
        ).map((indicator) => indicator.ind_code);

        if (isChecked) {
            if (categoryId === 32) {
                const formattedKeyIndicators = keyIndicators.map((indicator) => indicator.ind_code);
                setSelectedCategories((prev) => [...prev, categoryId]);
                form.setFieldValue('indicators', Array.from(new Set([...currentIndicators, ...formattedKeyIndicators])));
            }
            else if (categoryId === 27) {
                setSelectedCategories((prev) => [...prev, categoryId]);
            }
            else {
                setSelectedCategories((prev) => [...prev, categoryId]);
                form.setFieldValue('indicators', Array.from(new Set([...currentIndicators, ...indicatorsInCategory])));
            }
        } else {
            if (categoryId === 32) {
                const formattedKeyIndicators = keyIndicators.map((indicator) => indicator.ind_code);
                setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
                form.setFieldValue('indicators', currentIndicators.filter((ind) => !formattedKeyIndicators.includes(ind)));
            }
            setSelectedCategories((prev) => prev.filter((id) => id !== categoryId));
            form.setFieldValue('indicators', currentIndicators.filter((ind) => !indicatorsInCategory.includes(ind)));
        }
    };

    const handleGoalSelected = (categoryId, goals) => {
        const currentIndicators = form.getFieldValue('indicators') || [];
        const goalsArray = Array.isArray(goals) ? goals : (goals ? [goals.toString()] : []);

        const allSdgPatterns = Array.from({ length: 17 }, (_, i) => new RegExp(`^SDG\\s*0*${i + 1}\\.`, 'i'));
        const allSdgIndicators = Object.values(indicatorsState.value || {}).filter(
            (indicator) => allSdgPatterns.some(pattern => pattern.test(indicator.ind_name_eng))
        ).map((indicator) => indicator.ind_code);

        const selectedPatterns = goalsArray.map(goal => new RegExp(`^SDG\\s*0*${goal}\\.`, 'i'));
        const selectedSdgIndicators = Object.values(indicatorsState.value || {}).filter(
            (indicator) => selectedPatterns.some(pattern => pattern.test(indicator.ind_name_eng))
        ).map((indicator) => indicator.ind_code);

        const filteredIndicators = currentIndicators.filter(ind => !allSdgIndicators.includes(ind));
        form.setFieldValue('indicators', Array.from(new Set([...filteredIndicators, ...selectedSdgIndicators])));
    };


    const renderTagPlaceholder = (omittedValues) => {
        const sortedOmittedValues = [...omittedValues].sort((a, b) => a.value - b.value);
        const fullList = sortedOmittedValues.map(val => val.label).join('\n');
        return (
            <span>{`+${omittedValues.length} more`}</span>
        );
    };

    const handleCarriedDirectionChange = (value, index) => {
        const yearConfigs = form.getFieldValue('year_configs') || [];
        const updatedConfigs = [...yearConfigs];

        if (value === 'backward') {
            updatedConfigs[index] = { ...updatedConfigs[index], forward_year: undefined };
        } else if (value === 'forward') {
            updatedConfigs[index] = { ...updatedConfigs[index], backward_year: undefined };
        }

        form.setFieldValue('year_configs', updatedConfigs);
    };

    const removeYearConfig = (index) => {
        const yearConfigs = form.getFieldValue('year_configs') || [];
        const updatedConfigs = yearConfigs.filter((_, i) => i !== index);
        form.setFieldValue('year_configs', updatedConfigs);
    };

    const handleSubmit = async (values) => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setLoading(true);
        console.log('Form Values:', values);

        const year = values.year_configs[0].year;
        const backwardYear = values.year_configs[0].backward_year;
        const forwardYear = values.year_configs[0].forward_year;

        console.log('Year', year);
        console.log('Backward Year', backwardYear);
        console.log('Forward Year', forwardYear);

        const buildReferenceDisplayFromForm = (configs) => {
            if (!configs || configs.length === 0) return 'N/A';
            const parts = configs.map(cfg => {
                const b = cfg.backward_year;
                const f = cfg.forward_year;
                if (b && f) return `${b}-${f}`;
                if (b) return String(b);
                if (f) return String(f);
                return null;
            }).filter(Boolean);
            return parts.length ? parts.join(', ') : 'N/A';
        };

        if(values.return_interval_years){
            
        }

        const frontendReferenceDisplay = buildReferenceDisplayFromForm(values.year_configs);
        const frontendReferenceRaw = (values.year_configs || []).map(cfg => ({ backward: cfg.backward_year || null, forward: cfg.forward_year || null }));

        if (forwardYear <= year || backwardYear >= year) {
            message.warning('Invalid year configuration: Ensure backward year is less than the selected year and forward year is greater than the selected year.');
            setLoading(false);
            submittingRef.current = false;
            return;
        }

        const generateIntervalYearConfigs = (configs) => {
            const allConfigs = [];
            
            configs.forEach(config => {
                const baseYear = config.year;
                const direction = config.carried_direction;
                const backwardYear = config.backward_year;
                const forwardYear = config.forward_year;
                
                allConfigs.push(config);
                
                if (direction === 'backward' && backwardYear) {
                    for (let y = baseYear - 1; y > backwardYear; y--) {
                        allConfigs.push({
                            year: y,
                            carried_direction: 'backward',
                            backward_year: backwardYear,
                            forward_year: undefined
                        });
                    }
                } else if (direction === 'forward' && forwardYear) {
                    for (let y = baseYear + 1; y < forwardYear; y++) {
                        allConfigs.push({
                            year: y,
                            carried_direction: 'forward',
                            forward_year: forwardYear,
                            backward_year: undefined
                        });
                    }
                } else if (direction === 'both' && backwardYear && forwardYear) {
                    for (let y = baseYear - 1; y > backwardYear; y--) {
                        allConfigs.push({
                            year: y,
                            carried_direction: 'backward',
                            backward_year: backwardYear,
                            forward_year: undefined
                        });
                    }
                    for (let y = baseYear + 1; y < forwardYear; y++) {
                        allConfigs.push({
                            year: y,
                            carried_direction: 'forward',
                            forward_year: forwardYear,
                            backward_year: undefined
                        });
                    }
                }
            });
            
            return allConfigs;
        };

        const yearConfigsToUse = values.return_interval_years 
            ? generateIntervalYearConfigs(values.year_configs)
            : values.year_configs;

        const refDisplayByBaseYear = {};
        yearConfigsToUse.forEach(config => {
            const b = config.backward_year;
            const f = config.forward_year;
            let display = null;
            if (b && f) display = `${b}-${f}`;
            else if (b) display = String(b);
            else if (f) display = String(f);
            refDisplayByBaseYear[config.year] = display;
        });

        try {
            const requestData = {
                ind_codes: values.indicators,
                r_codes: values.regions,
                year_configs: yearConfigsToUse.map(config => {
                    const baseYear = config.year;
                    const direction = config.carried_direction;

                    let forwardYearAmount = null;
                    let backwardYearAmount = null;

                    if (direction === 'forward' && config.forward_year) {
                        forwardYearAmount = config.forward_year - baseYear;
                    } else if (direction === 'backward' && config.backward_year) {
                        backwardYearAmount = baseYear - config.backward_year;
                    } else if (direction === 'both') {
                        if (config.forward_year) {
                            forwardYearAmount = config.forward_year - baseYear;
                        }
                        if (config.backward_year) {
                            backwardYearAmount = baseYear - config.backward_year;
                        }
                    }

                    return {
                        year: baseYear,
                        carried_direction: direction,
                        forward_year: forwardYearAmount,
                        backward_year: backwardYearAmount
                    };
                }),
                sync_countries_year_calculations: values.sync_countries_year_calculations,
                return_interval_years: values.return_interval_years
            };

            const response = await AxiosOICStat.post('/carry', requestData);

            const transformedResults = [];

            response.data.forEach((item, responseIndex) => {
                const regionId = item.region_id;
                const indicatorId = item.indicator_id;
                const indicatorCode = item.indicator_code || indicatorId;
                const unitCode = item.unit || item.indicator_unit || item.u_code;
                const baseYear = item.base_year || item.base_years || [];
                const referenceYear = item.reference_year || item.reference_years || baseYear;
                const regionValue = item.region_value || item.aggregated_result_dict;
                const countriesCovered = item.countries_covered || item.length || 0;

                const rawReference = referenceYear;
                const computeRefDisplay = (raw, baseYearKey) => {
                    if (baseYearKey != null && refDisplayByBaseYear[baseYearKey]) return refDisplayByBaseYear[baseYearKey];
                    if (frontendReferenceDisplay && frontendReferenceDisplay !== 'N/A') return frontendReferenceDisplay;
                    if (!raw) return 'N/A';
                    if (Array.isArray(raw)) {
                        const parts = raw.map(r => {
                            if (r && typeof r === 'object' && (r.backward || r.forward)) {
                                const b = r.backward || '';
                                const f = r.forward || '';
                                return b && f ? `${b}-${f}` : (b || f || '');
                            }
                            return String(r);
                        }).filter(Boolean);
                        return parts.length ? parts.join(', ') : 'N/A';
                    }
                    return String(raw);
                };

                if (regionValue && typeof regionValue === 'object') {
                    Object.entries(regionValue).forEach(([year, result]) => {
                        transformedResults.push({
                            key: `${responseIndex}-${year}`,
                            r_code: regionId,
                            ind_code: indicatorCode,
                            year: parseInt(year),
                            region_result: result || 0,
                            length: countriesCovered,
                            u_code: unitCode,
                            years: item.years || [],
                            reference_years: frontendReferenceRaw.length ? frontendReferenceRaw : rawReference,
                            reference_years_display: computeRefDisplay(rawReference, year),
                            aggregate_method: item.aggregate_method || item.aggr_method || null,
                            weighted_indicator: item.weighted_indicator || null,
                            base_year: baseYear,
                            countries_covered: countriesCovered,
                            full_data: item
                        });
                    });
                } else {
                    transformedResults.push({
                        key: responseIndex,
                        r_code: regionId,
                        ind_code: indicatorCode,
                        year: item.years?.[0] || 'N/A',
                        region_result: regionValue || 0,
                        length: countriesCovered,
                        u_code: unitCode,
                        years: item.years || [],
                        reference_years: frontendReferenceRaw.length ? frontendReferenceRaw : rawReference,
                        reference_years_display: computeRefDisplay(rawReference, item.years?.[0]),
                        aggregate_method: item.aggregate_method || item.aggr_method || null,
                        weighted_indicator: item.weighted_indicator || null,
                        base_year: baseYear,
                        countries_covered: countriesCovered,
                        full_data: item
                    });
                }
            });

            setResults(transformedResults);
            if (transformedResults.length > 0) showCrossCheckNotice();
        } catch (error) {
            console.error('Error fetching carry data:', error);
            message.error('Failed to fetch carry forward data');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    useEffect(() => {
        if (results && results.length > 0) {
            const exportService = CarryExportService(results, countriesState.value, indicatorsState.value, unitsState.value, regionMappingsData);
            setCarryExportService(exportService);
        } else {
            setCarryExportService(null);
        }
    }, [results, countriesState, indicatorsState, unitsState, regionMappingsData]);

    const handleResultExport = () => {
        if (carryExportService) {
            carryExportService.exportTableResults();
        } else {
            message.warning('No data available for export');
        }
    };

    const handleDetailExport = () => {
        if (carryExportService) {
            carryExportService.exportDetailsToExcel();
        } else {
            message.warning('No data available for export');
        }
    };
    const columns = [
        {
            title: 'Region Name',
            dataIndex: 'r_code',
            key: 'region_name',
            sorter: (a, b) => {
                const regionA = regionMappingsData.find(r => r.id === a.r_code);
                const regionB = regionMappingsData.find(r => r.id === b.r_code);
                const nameA = regionA ? regionA.region_name : a.r_code;
                const nameB = regionB ? regionB.region_name : b.r_code;
                return nameA.localeCompare(nameB);
            },
            render: (rCode) => {
                const region = regionMappingsData.find(r => r.id === rCode);
                return region ? region.region_name : rCode;
            }
        },
        {
            title: 'Indicator Code',
            dataIndex: 'ind_code',
            key: 'ind_code',
            sorter: (a, b) => a.ind_code - b.ind_code,
        },
        {
            title: 'Indicator Name',
            dataIndex: 'ind_code',
            key: 'indicator_name',
            sorter: (a, b) => {
                const indicatorA = Object.values(indicatorsState.value || {}).find(ind => ind.ind_code === a.ind_code);
                const indicatorB = Object.values(indicatorsState.value || {}).find(ind => ind.ind_code === b.ind_code);
                const nameA = indicatorA ? getIndicatorName(indicatorA) : a.ind_code;
                const nameB = indicatorB ? getIndicatorName(indicatorB) : b.ind_code;
                return nameA.localeCompare(nameB);
            },
            render: (indCode) => {
                const indicator = Object.values(indicatorsState.value || {}).find(ind => ind.ind_code === indCode);
                return indicator ? getIndicatorName(indicator) : indCode;
            }
        },
        {
            title: 'Unit',
            dataIndex: 'u_code',
            key: 'unit',
            sorter: (a, b) => {
                const unitA = unitsState.value ? unitsState.value[a.u_code] : null;
                const unitB = unitsState.value ? unitsState.value[b.u_code] : null;
                const nameA = unitA ? unitA.unit_name_eng : a.u_code;
                const nameB = unitB ? unitB.unit_name_eng : b.u_code;
                return String(nameA).localeCompare(String(nameB));
            },
            render: (uCode) => {
                const unit = unitsState.value ? unitsState.value[uCode] : null;
                return unit ? unit.unit_name_eng : uCode;
            }
        },
        {
            title: 'Aggregate Method',
            dataIndex: 'aggregate_method',
            key: 'aggregate_method',
            sorter: (a, b) => {
                const methodA = a.aggregate_method || 'N/A';
                const methodB = b.aggregate_method || 'N/A';
                return methodA.localeCompare(methodB);
            },
            render: (method) => method || 'N/A'
        },
        {
            title: 'Weighted Indicator',
            dataIndex: 'weighted_indicator',
            key: 'weighted_indicator',
            sorter: (a, b) => {
                const weightA = a.weighted_indicator || 0;
                const weightB = b.weighted_indicator || 0;
                return weightA - weightB;
            },
            render: (value) => {
                if (!value) return 'N/A';
                const indicator = Object.values(indicatorsState.value || {}).find(ind => ind.ind_code === value);
                return indicator ? `${value} - ${getIndicatorName(indicator)}` : value;
            }
        },
        {
            title: 'Base Year',
            dataIndex: 'year',
            key: 'year',
            sorter: (a, b) => {
                const baseA = Array.isArray(a.year) ? a.year.join(',') : 'N/A';
                const baseB = Array.isArray(b.year) ? b.year.join(',') : 'N/A';
                return baseA.localeCompare(baseB);
            },
            render: (baseYear) => {
                if (!baseYear || (Array.isArray(baseYear) && baseYear.length === 0)) return 'N/A';
                return Array.isArray(baseYear) ? baseYear.join(', ') : String(baseYear);
            }
        },
        {
            title: 'Reference Year',
            dataIndex: 'reference_years_display',
            key: 'reference_years',
            sorter: (a, b) => {
                const refA = a.reference_years_display || 'N/A';
                const refB = b.reference_years_display || 'N/A';
                return refA.localeCompare(refB);
            },
            render: (refs) => refs || 'N/A'
        },
        {
            title: 'Region Result',
            dataIndex: 'region_result',
            key: 'region_result',
            sorter: (a, b) => a.region_result - b.region_result,
            render: (value) => typeof value === 'number' ? value.toFixed(6) : value
        },
        {
            title: 'Countries Count',
            dataIndex: 'countries_covered',
            key: 'countries_covered',
            sorter: (a, b) => (a.countries_covered || 0) - (b.countries_covered || 0),
            render: (value) => value || 0
        },
    ];

    const indicatorOptions = Object.values(indicatorsState.value || {})
        .filter(indicator => indicator.aggr_method && indicator.aggr_formula)
        .map(indicator => ({
            label: `${indicator.ind_code} - ${getIndicatorName(indicator)}`,
            value: indicator.ind_code
        }));

    const regionOptions = regionMappingsData.map(region => ({
        label: region.region_name,
        value: region.id
    }));

    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let year = 1970; year <= currentYear; year++) {
        yearOptions.push({ label: year, value: year });
    }

    return (
        <div style={{ padding: '20px' }}>
            <Card title="Carry Forward-Backward Aggregation" style={{ marginBottom: '20px' }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        year_configs: [{
                            year: currentYear - 1,
                            carried_direction: 'backward',
                            forward_year: undefined,
                            backward_year: undefined
                        }],
                        sync_countries_year_calculations: true,
                        return_interval_years: false
                    }}
                >
                    <CategoriesCollapse selectedCategories={selectedCategories} onChange={handleCategoryChange} onGoalSelected={handleGoalSelected} />

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="regions"
                                label="Regions"
                                rules={[{ required: true, message: 'Please select at least one region' }]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select Regions"
                                    options={regionOptions}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="indicators"
                                label="Indicators"
                                rules={[{ required: true, message: 'Please select at least one indicator' }]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select Indicators"
                                    options={indicatorOptions}
                                    maxTagCount={2}
                                    maxTagPlaceholder={renderTagPlaceholder}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onSearch={setIndicatorSearchFilter}
                                    autoClearSearchValue={false}
                                    dropdownRender={(menu) => {
                                        const filteredOptions = indicatorOptions.filter(option =>
                                            option.label.toLowerCase().includes(indicatorSearchFilter.toLowerCase())
                                        );
                                        const selectedValues = form.getFieldValue('indicators') || [];
                                        const allFiltered = filteredOptions.length > 0 && filteredOptions.every(opt => selectedValues.includes(opt.value));
                                        return (
                                            <>
                                                <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                                    <Checkbox
                                                        checked={allFiltered}
                                                        indeterminate={filteredOptions.some(opt => selectedValues.includes(opt.value)) && !allFiltered}
                                                        onChange={(e) => {
                                                            const currentValue = form.getFieldValue('indicators') || [];
                                                            if (e.target.checked) {
                                                                const newValues = [...new Set([...currentValue, ...filteredOptions.map(opt => opt.value)])];
                                                                form.setFieldValue('indicators', newValues);
                                                            } else {
                                                                const newValues = currentValue.filter(ind => !filteredOptions.map(opt => opt.value).includes(ind));
                                                                form.setFieldValue('indicators', newValues);
                                                            }
                                                        }}
                                                    >
                                                        <span style={{ fontSize: '12px' }}>Select All in Results</span>
                                                    </Checkbox>
                                                </div>
                                                {menu}
                                            </>
                                        );
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Year Configurations</Divider>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="return_interval_years" valuePropName="checked">
                                <Checkbox>
                                    Add every year in the carry range as a year configuration
                                    <Tooltip title="Each year between the selected Year and its Backward/Forward Year is also calculated, carrying to the same limit. E.g. Year 2020 with Backward Year 2015 gives results for 2015–2020.">
                                        <InfoCircleOutlined style={{ marginLeft: 5 }} />
                                    </Tooltip>
                                </Checkbox>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="sync_countries_year_calculations" valuePropName="checked">
                                <Checkbox>
                                    Use the same countries in every year configuration
                                    <Tooltip title="A country with no value (even after carrying) in any year configuration is left out of all of them, so every year is calculated with the same countries. When off, each year uses every country that has data, so the country count can differ between years.">
                                        <InfoCircleOutlined style={{ marginLeft: 5 }} />
                                    </Tooltip>
                                </Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.List name="year_configs">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => (
                                    <Card
                                        key={key}
                                        size="small"
                                        style={{ marginBottom: '16px' }}
                                        title={`Year Configuration ${index + 1}`}
                                        extra={
                                            fields.length > 1 && (
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => {
                                                        remove(name);
                                                        removeYearConfig(index);
                                                    }}
                                                />
                                            )
                                        }
                                    >
                                        <Row gutter={16}>
                                            <Col span={6}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'year']}
                                                    label="Year"
                                                    rules={[{ required: true, message: 'Please select a year' }]}
                                                >
                                                    <Select
                                                        placeholder="Select Year"
                                                        options={yearOptions}
                                                        showSearch
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'carried_direction']}
                                                    label="Carry Direction"
                                                    rules={[{ required: true, message: 'Please select carry direction' }]}
                                                >
                                                    <Select
                                                        placeholder="Select Direction"
                                                        onChange={(value) => handleCarriedDirectionChange(value, index)}
                                                    >
                                                        <Option value="backward">Backward</Option>
                                                        <Option value="forward">Forward</Option>
                                                        <Option value="both">Both</Option>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item
                                                    shouldUpdate={(prevValues, currentValues) => {
                                                        const prevDirection = prevValues.year_configs?.[index]?.carried_direction;
                                                        const currentDirection = currentValues.year_configs?.[index]?.carried_direction;
                                                        return prevDirection !== currentDirection;
                                                    }}
                                                >
                                                    {({ getFieldValue }) => {
                                                        const direction = getFieldValue(['year_configs', index, 'carried_direction']);
                                                        return (
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'backward_year']}
                                                                label="Backward Years"
                                                                rules={direction === 'both' ? [{ required: true, message: 'Required' }] : []}
                                                            >
                                                                <Select
                                                                    options={yearOptions}
                                                                    placeholder="Years backward"
                                                                    showSearch
                                                                    style={{ width: '100%' }}
                                                                    disabled={direction === 'forward'}
                                                                />
                                                            </Form.Item>
                                                        );
                                                    }}
                                                </Form.Item>
                                            </Col>
                                            <Col span={6}>
                                                <Form.Item
                                                    shouldUpdate={(prevValues, currentValues) => {
                                                        const prevDirection = prevValues.year_configs?.[index]?.carried_direction;
                                                        const currentDirection = currentValues.year_configs?.[index]?.carried_direction;
                                                        return prevDirection !== currentDirection;
                                                    }}
                                                >
                                                    {({ getFieldValue }) => {
                                                        const direction = getFieldValue(['year_configs', index, 'carried_direction']);
                                                        return (
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'forward_year']}
                                                                label="Forward Years"
                                                                rules={direction === 'both' ? [{ required: true, message: 'Required' }] : []}
                                                            >
                                                                <Select
                                                                    options={yearOptions}
                                                                    placeholder="Years forward"
                                                                    showSearch
                                                                    style={{ width: '100%' }}
                                                                    disabled={direction === 'backward'}
                                                                />
                                                            </Form.Item>
                                                        );
                                                    }}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                ))}
                                <Form.Item>
                                    <Button
                                        type="dashed"
                                        onClick={() => {
                                            add();
                                        }}
                                        icon={<PlusOutlined />}
                                        style={{ width: '100%' }}
                                    >
                                        Add Year Configuration
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Calculate Carry Calculation
                            </Button>
                            <Button
                                type="default"
                                onClick={handleResultExport}
                                loading={loading}
                                disabled={results.length === 0}
                            >
                                Export Table Results
                            </Button>
                            <Button
                                type="default"
                                onClick={handleDetailExport}
                                loading={loading}
                                disabled={results.length === 0}
                            >
                                Export Details
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            {results.length > 0 && (
                <Card title="Carry Forward Results">
                    <Table
                        columns={columns}
                        dataSource={results.map((item, index) => ({ ...item, key: index }))}
                        loading={loading}
                        pagination={false}
                        size="small"
                    />
                </Card>
            )}
        </div>
    );
}